import React from 'react';
import { fromEntries } from 'ricos-content/libs/utils';
import { Decoration_Type, Node_Type } from 'ricos-schema';
import type {
  EditorCommands,
  FormattingToolbarButton,
  FormattingToolbarButtonConfig,
  FormattingToolbarButtons,
  IEditorQuery,
  IToolbarItemConfigTiptap,
  KeyboardShortcut,
  ModalConfig,
  Platform,
  TiptapEditor,
  ToolbarButtonProps,
  ToolbarSettings,
  ToolbarSettingsFunctions,
  ToolbarType,
} from 'ricos-types';
import { FORMATTING_BUTTONS, TOOLBARS } from 'wix-rich-content-editor-common';
import {
  defaultToolbarItemsConfigs,
  FormattingToolbar,
  resolversById,
} from 'wix-rich-content-toolbars-v3';
import { RESOLVERS_IDS } from 'wix-rich-content-toolbars-v3/libs/resolvers-ids';

import type { PluginServices } from './editorPlugins';
import { cleanSeparators } from './toolbar-utils/cleanSeparators';
import { getToolbarConfig } from './toolbar-utils/getToolbarConfig';
import { initToolbarSettings } from './toolbar-utils/initToolbarSettings';
import {
  toExternalToolbarItemsConfig,
  toTiptapToolbarItemsConfig,
} from './toolbar-utils/toToolbarItemsConfig';

const toExternalType = {
  [Decoration_Type.BOLD]: FORMATTING_BUTTONS.BOLD,
  [Decoration_Type.ITALIC]: FORMATTING_BUTTONS.ITALIC,
  [Decoration_Type.UNDERLINE]: FORMATTING_BUTTONS.UNDERLINE,
  [`${Node_Type.HEADING}.title`]: FORMATTING_BUTTONS.TITLE,
  [Node_Type.BLOCKQUOTE]: FORMATTING_BUTTONS.BLOCKQUOTE,
  'TextAlignment.ALIGNMENT': FORMATTING_BUTTONS.ALIGNMENT,
  'TextAlignment.LEFT': FORMATTING_BUTTONS.ALIGN_LEFT,
  'TextAlignment.CENTER': FORMATTING_BUTTONS.ALIGN_CENTER,
  'TextAlignment.RIGHT': FORMATTING_BUTTONS.ALIGN_RIGHT,
  'TextAlignment.JUSTIFY': FORMATTING_BUTTONS.ALIGN_JUSTIFY,
  [Node_Type.ORDERED_LIST]: FORMATTING_BUTTONS.ORDERED_LIST,
  [Node_Type.BULLETED_LIST]: FORMATTING_BUTTONS.UNORDERED_LIST,
  [Decoration_Type.FONT_SIZE]: FORMATTING_BUTTONS.FONT_SIZE,
  [Decoration_Type.SPOILER]: FORMATTING_BUTTONS.SPOILER,
  [Decoration_Type.LINK]: FORMATTING_BUTTONS.LINK,
  [`${Node_Type.HEADING}.dropdown`]: FORMATTING_BUTTONS.HEADINGS,
  [`${Decoration_Type.COLOR}.foreground`]: FORMATTING_BUTTONS.TEXT_COLOR,
  [`${Decoration_Type.COLOR}.background`]: FORMATTING_BUTTONS.TEXT_HIGHLIGHT,
  [Node_Type.CODE_BLOCK]: FORMATTING_BUTTONS.CODE_BLOCK,
  LINE_SPACING: FORMATTING_BUTTONS.LINE_SPACING,
  [`INDENT.increase`]: FORMATTING_BUTTONS.INCREASE_INDENT,
  [`INDENT.decrease`]: FORMATTING_BUTTONS.DECREASE_INDENT,
};

export class PluginTextButton implements FormattingToolbarButton {
  private readonly button: FormattingToolbarButtonConfig;

  private services: PluginServices;

  private readonly tooltipHint: string;

  private constructor(
    button: FormattingToolbarButtonConfig,
    services: PluginServices,
    platform: Platform
  ) {
    this.button = button;
    this.services = services;
    this.tooltipHint = services.shortcuts.getShortcutDisplayData(
      button.id as KeyboardShortcut['name'],
      this.services.t,
      platform
    ).tooltipHint;
  }

  static of(button: FormattingToolbarButtonConfig, services: PluginServices, platform: Platform) {
    return new PluginTextButton(button, services, platform);
  }

  private getTooltip() {
    return this.button.presentation?.tooltip
      ? `${this.services.t(this.button.presentation.tooltip)} ${
          this.tooltipHint ? `(${this.tooltipHint})` : ''
        }`
      : '';
  }

  getButtonId() {
    return this.button.id;
  }

  register() {
    const modal = this.button.modal;
    if (modal) {
      this.services.modals.register(modal);
    }
  }

  unregister() {
    if (this.button.modal) {
      this.services.modals.unregister(this.button.modal?.id);
    }
  }

  private toResolvedAttributes(options?: {
    disabled: boolean;
  }): IToolbarItemConfigTiptap['attributes'] {
    const resolvedAttributes = Object.entries(this.button.attributes || {}).map(([key, value]) => {
      return [key, resolversById[value]];
    });
    if (options?.disabled) {
      resolvedAttributes.push(['disabled', resolversById[RESOLVERS_IDS.ALWAYS_VISIBLE]]);
    }
    return fromEntries(resolvedAttributes);
  }

  private resolveModalLayout(isMobile: boolean, modal?: ModalConfig, referenceElement?: any) {
    if (isMobile) {
      if (modal?.layout && typeof modal.layout === 'object') {
        return modal.layout.mobile;
      }
      return 'drawer';
    }

    if (referenceElement) {
      return 'toolbar';
    }

    if (modal?.layout && typeof modal.layout === 'object') {
      return modal.layout.desktop;
    }

    return modal?.layout ?? 'dialog';
  }

  toToolbarItemConfig(
    editorCommands: EditorCommands,
    editor: TiptapEditor,
    toolbarType: ToolbarType,
    options?: { disabled: boolean }
  ): IToolbarItemConfigTiptap {
    const { modals, context } = this.services;
    return {
      ...this.button,
      presentation: {
        ...(this.button.presentation || {}),
        tooltip: this.getTooltip(),
      },
      attributes: this.toResolvedAttributes(options),
      commands: {
        ...this.button.commands,
        click:
          ({ editorQuery }) =>
          ({ referenceElement, clickFromKeyboard, ...rest } = {}) => {
            this.services.toolbars.byType(toolbarType).publishButtonClick(this.button.id);

            if (this.button.command) {
              return this.button.command?.({
                editorCommands,
                modalService: modals,
                editor,
                editorQuery,
              })(rest);
            }

            if (!this.button.modal) {
              return;
            }

            return modals.isModalOpen(this.button.modal.id)
              ? modals.closeModal(this.button.modal.id)
              : modals.openModal(this.button.modal.id, {
                  componentProps: {
                    closeModal: () => this.button.modal && modals.closeModal(this.button.modal.id),
                    editorCommands,
                  },
                  positioning: {
                    placement: 'bottom',
                    referenceElement,
                  },
                  layout: this.resolveModalLayout(
                    context.isMobile,
                    this.button.modal,
                    referenceElement
                  ),
                  returnFocusOnDeactivate: clickFromKeyboard,
                });
          },
      },
    };
  }

  toExternalToolbarButtonConfig(
    editorCommands: EditorCommands,
    editor: TiptapEditor,
    editorQuery: IEditorQuery
  ): ToolbarButtonProps {
    const attributes = this.toResolvedAttributes();
    const { modals, content, context, t } = this.services;
    const name = toExternalType[this.button.id] ?? this.button.id;

    return {
      type: 'button',
      tooltip: this.getTooltip(),
      toolbars: [],
      getIcon: () =>
        this.button.presentation?.icon ||
        this.button.presentation?.getIcon(editorCommands, this.services.t),
      getLabel: () => t(name),
      onClick: e => {
        this.services.toolbars.external.publishButtonClick(this.button.id);

        if (this.button.command) {
          return this.button.command?.({
            editorCommands,
            modalService: modals,
            editor,
            editorQuery,
          })({});
        }

        if (!this.button.modal) {
          return;
        }

        return modals.isModalOpen(this.button.modal.id)
          ? modals.closeModal(this.button.modal.id)
          : modals.openModal(this.button.modal.id, {
              componentProps: {
                closeModal: () => this.button.modal && modals.closeModal(this.button.modal.id),
              },
              positioning: {
                placement: 'bottom',
                referenceElement: e?.target,
              },
              layout: this.resolveModalLayout(context.isMobile, this.button.modal, e?.target),
            });
      },
      isActive: () => !!attributes.active && !!content.resolve(attributes.active),
      isDisabled: () => !!attributes.disabled && !!content.resolve(attributes.disabled),
      dataHook: this.button.presentation?.dataHook,
      name,
    };
  }
}

export class PluginTextButtons implements FormattingToolbarButtons {
  private readonly buttons: PluginTextButton[];

  services: PluginServices;

  private readonly finalToolbarSettings: ToolbarSettingsFunctions[];

  private constructor(
    buttons: PluginTextButton[],
    services: PluginServices,
    toolbarSettings: ToolbarSettings
  ) {
    this.buttons = buttons;
    this.services = services;
    this.finalToolbarSettings = initToolbarSettings(toolbarSettings);
  }

  static of(
    buttons: PluginTextButton[] = [],
    services: PluginServices,
    toolbarSettings: ToolbarSettings
  ): PluginTextButtons {
    return new PluginTextButtons(buttons, services, toolbarSettings);
  }

  asArray(): FormattingToolbarButton[] {
    return this.buttons;
  }

  toToolbarItemsConfig(
    toolbarType: ToolbarType,
    isMobile: boolean,
    editorCommands: EditorCommands,
    editor: TiptapEditor,
    options?: { disabled: boolean }
  ) {
    const toolbarConfig = getToolbarConfig(this.finalToolbarSettings, toolbarType);
    const toolbarItemConfigs = [
      ...this.buttons.map(b => b.toToolbarItemConfig(editorCommands, editor, toolbarType, options)),
      ...defaultToolbarItemsConfigs,
    ];
    const toolbarItemsConfig = toTiptapToolbarItemsConfig(
      toolbarConfig,
      toolbarItemConfigs,
      toolbarType,
      isMobile ? 'mobile' : 'desktop'
    );

    return cleanSeparators(toolbarItemsConfig);
  }

  toExternalToolbarButtonsConfigs(
    editorCommands: EditorCommands,
    editor: TiptapEditor,
    isMobile: boolean,
    editorQuery: IEditorQuery
  ): Record<string, ToolbarButtonProps> {
    const toolbarType = TOOLBARS.FORMATTING;
    const toolbarConfig = getToolbarConfig(this.finalToolbarSettings, toolbarType);

    const buttonsType = isMobile ? 'mobile' : 'desktop';

    const externalToolbarItemsConfig = toExternalToolbarItemsConfig(
      toolbarConfig,
      this.buttons,
      toolbarType,
      buttonsType
    );

    return externalToolbarItemsConfig.reduce((acc, b) => {
      const buttonConfig = b.toExternalToolbarButtonConfig(editorCommands, editor, editorQuery);
      return {
        ...acc,
        [buttonConfig.name || '']: buttonConfig,
      };
    }, {});
  }

  registerFormattingToolbarModal() {
    const { modals } = this.services;
    modals.register({
      id: 'ricos-formatting-toolbar',
      Component: props => {
        return <FormattingToolbar {...props} />;
      },
    });
  }
}
